import { apiFetchJson } from '@wundo/client/api.ts';

/** Manual markers: the ledger has no data before these dates. */
export const LLM_COSTS_SINCE = '22 Jun 2026';
export const OTHER_COSTS_SINCE = 'Oct 2026';

export const COSTS_SINCE_NOTE = `LLM costs since ${LLM_COSTS_SINCE}; notetaker, transcription and voice since ${OTHER_COSTS_SINCE}.`;

/** Total cost (USD) over standard time windows. */
export interface UsageCostWindows {
  today: number;
  last_7d: number;
  last_30d: number;
  all_time: number;
}

export type UsageCostRange = 'today' | 'last_7d' | 'last_30d' | 'this_month' | 'all_time';

export type UsageUnit = 'tokens' | 'characters' | 'audio_seconds' | 'bot_seconds';

export interface UsageCostTotals {
  cost_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_5m_tokens: number;
  cache_creation_1h_tokens: number;
  reasoning_output_tokens: number;
  unpriced_calls: number;
}

export interface UsageCostDaily {
  day: string;
  cost_usd: number;
  calls: number;
}

export interface UsageCostByVendor {
  vendor: string;
  unit: UsageUnit;
  cost_usd: number;
  quantity: number;
  calls: number;
}

export interface UsageCostByProduct {
  product: string;
  vendor: string;
  unit: UsageUnit;
  cost_usd: number;
  quantity: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
}

export interface UsageCostByOrganisation {
  organisation_id: string | null;
  organisation_name: string | null;
  cost_usd: number;
  calls: number;
}

export interface UsageCostByUser {
  user_id: string;
  name: string | null;
  email: string | null;
  organisation_name: string | null;
  cost_usd: number;
}

export interface UsageCostByGroup {
  key: string | null;
  cost_usd: number;
  calls: number;
}

export interface LlmLatencyByModel {
  model: string;
  decode_p50: number | null;
  decode_p05: number | null;
  decode_samples: number;
  ttft_p50: number | null;
  ttft_p95: number | null;
  ttft_samples: number;
}

export interface UsageCostDashboard {
  range: UsageCostRange;
  organisation_id: string | null;
  vendor: string | null;
  totals: UsageCostTotals;
  daily: UsageCostDaily[];
  by_vendor: UsageCostByVendor[];
  by_product: UsageCostByProduct[];
  by_organisation: UsageCostByOrganisation[];
  by_user: UsageCostByUser[];
  by_channel: UsageCostByGroup[];
  by_purpose: UsageCostByGroup[];
  by_mode: UsageCostByGroup[];
  latency_by_model: LlmLatencyByModel[];
}

export interface UsageCostFilters {
  organisationId: string | null;
  vendor: string | null;
}

export function fetchUserUsageCost(userId: string): Promise<UsageCostWindows> {
  return apiFetchJson<UsageCostWindows>(`/usage-costs/users/${userId}`);
}

export function fetchOrganisationUsageCost(
  organisationId: string,
): Promise<UsageCostWindows> {
  return apiFetchJson<UsageCostWindows>(`/usage-costs/organisations/${organisationId}`);
}

export function fetchUsageCostDashboard(
  range: UsageCostRange,
  filters: UsageCostFilters,
): Promise<UsageCostDashboard> {
  const params = new URLSearchParams({ range });
  if (filters.organisationId) params.set('organisation_id', filters.organisationId);
  if (filters.vendor) params.set('vendor', filters.vendor);
  return apiFetchJson<UsageCostDashboard>(`/usage-costs/dashboard?${params.toString()}`);
}
